import { useQuery } from '@tanstack/react-query'
import { absenceTypesApi, type AbsenceTypeBase } from '@/services/api/absenceTypes'

// Statische Fallback-Liste der 6 Default-Arten (R4: offline/fehler-robust).
// Q1: 'Privat' (vereinheitlicht von 'Persönlich').
export const DEFAULT_ABSENCE_OPTIONS: AbsenceTypeBase[] = [
  { id: 'illness', key: 'illness', label: 'Krankheit', sort_order: 0 },
  { id: 'training', key: 'training', label: 'Fortbildung', sort_order: 1 },
  { id: 'business_trip', key: 'business_trip', label: 'Dienstreise', sort_order: 2 },
  { id: 'vacation', key: 'vacation', label: 'Urlaub', sort_order: 3 },
  { id: 'personal', key: 'personal', label: 'Privat', sort_order: 4 },
  { id: 'other', key: 'other', label: 'Andere', sort_order: 5 },
]

export function defaultAbsenceLabel(key: string | null | undefined): string {
  if (!key) return '—'
  return DEFAULT_ABSENCE_OPTIONS.find(o => o.key === key)?.label ?? key
}

/**
 * Aktive Abwesenheitsarten der Schule (Lehrer-Kontext: Dropdowns/Chips/Labels).
 * Fällt bei Lade-/Fehlerfall auf die statische Default-Liste zurück (R4).
 * v3/F6 (bewusst): Während isLoading werden die 6 Defaults angezeigt — auch
 * wenn die Schule deaktivierte/eigene Arten hat (kurzer Flash, offline-robust);
 * die Backend-Validierung fängt veraltete Auswahl beim Submit ab (422).
 */
export function useAbsenceTypes() {
  const query = useQuery({
    queryKey: ['absence-types'],
    queryFn: absenceTypesApi.list,
    staleTime: 60 * 1000,
  })

  const types: AbsenceTypeBase[] =
    query.data?.absence_types?.length
      ? (query.data.absence_types as AbsenceTypeBase[])
      : DEFAULT_ABSENCE_OPTIONS

  const labelFor = (key: string | null | undefined): string => {
    if (!key) return '—'
    return types.find(t => t.key === key)?.label ?? defaultAbsenceLabel(key)
  }

  return {
    types,
    isLoading: query.isLoading,
    isError: query.isError,
    labelFor,
  }
}
